import React from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";

import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import RotateRightIcon from "@mui/icons-material/RotateRight";

import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import { AiFillWarning } from "react-icons/ai";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/cjs/Page/TextLayer.css";
import { QuickLookEntry } from "./quickLookContext";
import { configurePdfWorker } from "../pdfWorkerConfig";

configurePdfWorker();

type OpenDoc = { id: string; entry: QuickLookEntry };
type WindowWithFind = Window &
  typeof globalThis & {
    find?: (
      string: string,
      caseSensitive?: boolean,
      backwards?: boolean,
      wrapAround?: boolean,
      wholeWord?: boolean,
      searchInFrames?: boolean,
      showDialog?: boolean
    ) => boolean;
  };
type PdfTextItem = { str?: string; [key: string]: unknown };
type PdfViewport = {
  width: number;
  height: number;
  convertToViewportRectangle?: (rect: [number, number, number, number]) => number[];
};
type PdfPage = {
  getTextContent: () => Promise<{ items: PdfTextItem[] }>;
  getViewport?: (params: { scale: number; rotation?: number }) => PdfViewport;
};
type PdfDocument = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfPage>;
};
type SearchMatch = {
  pageNumber: number;
  itemIndex: number;
  matchIndexInItem: number;
  charIndexInItem: number;
};
type SearchHighlightRect = {
  pageNumber: number;
  matchIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

const MAIN_PAGE_WIDTH = 800;
const THUMBNAIL_WIDTH = 110;
// US Letter aspect ratio as a stand-in until the first real page is measured.
const ESTIMATED_PAGE_RATIO = 11 / 8.5;
// Render real pages only this close to the current page; placeholders elsewhere.
const MAIN_RENDER_WINDOW = 2;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;

export const isPdfUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    // Query-string/hash safe: "x.pdf?sig=..." and "x.pdf#page=2" are PDFs.
    const { pathname } = new URL(url, window.location.origin);
    return /\.pdf$/i.test(pathname);
  } catch {
    return /\.pdf($|\?)/i.test(url);
  }
};

const formatUploadedAt = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  let hour = date.getHours();
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return "Uploaded " + month + "/" + day + "/" + year + " " + hour + ":" + minute + ":" + second + " " + period;
};

const hasDuplicateFlag = (value?: boolean | number | string | null): boolean => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "yes";
  }
  return false;
};

const countOccurrences = (value: string, query: string): number => {
  if (!value || !query) return 0;
  const text = value.toLowerCase();
  const needle = query.toLowerCase();
  let count = 0;
  let index = text.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = text.indexOf(needle, index + needle.length);
  }
  return count;
};

const getNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const buildHighlightRect = (
  page: PdfPage,
  item: PdfTextItem,
  query: string,
  charIndexInItem: number,
  pageWidth: number,
  rotation: number
): Omit<SearchHighlightRect, "pageNumber" | "matchIndex"> | null => {
  if (!page.getViewport || !item.str || !Array.isArray(item.transform)) return null;

  const baseViewport = page.getViewport({ scale: 1, rotation });
  const scale = pageWidth / baseViewport.width;
  const viewport = page.getViewport({ scale, rotation });
  if (!viewport.convertToViewportRectangle) return null;

  const transform = item.transform as unknown[];
  const x = getNumber(transform[4]);
  const y = getNumber(transform[5]);
  const itemWidth = getNumber(item.width);
  const itemHeight = Math.abs(
    getNumber(item.height) ?? getNumber(transform[3]) ?? getNumber(transform[0]) ?? 0
  );

  if (x === null || y === null || itemWidth === null || itemHeight === null || item.str.length === 0) {
    return null;
  }

  const matchStartRatio = charIndexInItem / item.str.length;
  const matchEndRatio = (charIndexInItem + query.length) / item.str.length;
  const startX = x + itemWidth * matchStartRatio;
  const endX = x + itemWidth * matchEndRatio;
  const rect = viewport.convertToViewportRectangle([startX, y, endX, y + itemHeight]);
  const left = Math.min(rect[0], rect[2]);
  const top = Math.min(rect[1], rect[3]);
  const width = Math.max(2, Math.abs(rect[2] - rect[0]));
  const height = Math.max(8, Math.abs(rect[3] - rect[1]));

  return { left, top, width, height };
};

// Sidebar thumbnail that only mounts its <Page> once scrolled near the
// viewport, so a 100-page PDF doesn't rasterize 100 thumbnails up front.
const LazyThumbnail = ({
  pageNumber,
  placeholderHeight,
}: {
  pageNumber: number;
  placeholderHeight: number;
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [isNearViewport, setIsNearViewport] = React.useState(
    typeof IntersectionObserver === "undefined"
  );

  React.useEffect(() => {
    if (isNearViewport) return;
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isNearViewport]);

  return (
    <div ref={containerRef}>
      {isNearViewport ? (
        <Page
          pageNumber={pageNumber}
          width={THUMBNAIL_WIDTH}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      ) : (
        <div
          data-testid={`quick-look-thumbnail-placeholder-${pageNumber}`}
          style={{ width: THUMBNAIL_WIDTH, height: placeholderHeight }}
        />
      )}
    </div>
  );
};

const QuickLookDialog = ({
  openDoc,
  onClose,
  sourcePosition = 0,
  sourceTotal = 0,
}: {
  openDoc: OpenDoc | null;
  onClose: () => void;
  sourcePosition?: number;
  sourceTotal?: number;
}) => {
  const [numPages, setNumPages] = React.useState(1);
  const [currentPage, setCurrentPage] = React.useState(1);
  // Height of a rendered main page; placeholders use it so the scroll
  // geometry stays stable while pages outside the render window stay lazy.
  const [mainPageHeight, setMainPageHeight] = React.useState<number | null>(null);
  const [imgLoading, setImgLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [retryKey, setRetryKey] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [searchText, setSearchText] = React.useState("");
  const [searchMatches, setSearchMatches] = React.useState<SearchMatch[]>([]);
  const [searchHighlightRects, setSearchHighlightRects] = React.useState<SearchHighlightRect[]>([]);
  const [activeSearchIndex, setActiveSearchIndex] = React.useState(-1);
  const [isSearching, setIsSearching] = React.useState(false);
  const [pdfLoadKey, setPdfLoadKey] = React.useState(0);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const pdfDocumentRef = React.useRef<PdfDocument | null>(null);
  const searchRunRef = React.useRef(0);
  // Text content is invariant to zoom/rotation — only the highlight rects'
  // viewport math depends on those. Caching it keyed by page number means a
  // zoom/rotate while a search is active only recomputes rects, not the full
  // per-page text extraction.
  const pageTextContentCache = React.useRef<Map<number, { items: PdfTextItem[] }>>(new Map());
  const [debouncedSearchText, setDebouncedSearchText] = React.useState("");
  const mainAreaRef = React.useRef<HTMLDivElement | null>(null);
  const pageRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollRafRef = React.useRef<number | null>(null);

  const scrollToPage = React.useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    pageRefs.current.get(pageNumber)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  const handleZoomOut = React.useCallback(() => {
    setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
  }, []);

  const handleZoomIn = React.useCallback(() => {
    setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
  }, []);

  const handleActualSize = React.useCallback(() => {
    setZoom(1);
  }, []);

  const handleRotateRight = React.useCallback(() => {
    setRotation((current) => (current + 90) % 360);
  }, []);

  const handleBrowserFind = React.useCallback(
    (backward = false) => {
      const query = searchText.trim();
      const browserWindow = window as WindowWithFind;
      if (!query || typeof browserWindow.find !== "function") return;
      browserWindow.find(query, false, backward, true, false, false, false);
      searchInputRef.current?.focus();
    },
    [searchText]
  );

  const handleNavigateSearch = React.useCallback(
    (direction: 1 | -1) => {
      if (searchMatches.length > 0) {
        const nextIndex =
          activeSearchIndex === -1
            ? direction === 1
              ? 0
              : searchMatches.length - 1
            : (activeSearchIndex + direction + searchMatches.length) % searchMatches.length;
        const nextMatch = searchMatches[nextIndex];
        setActiveSearchIndex(nextIndex);
        if (nextMatch.pageNumber !== currentPage) {
          scrollToPage(nextMatch.pageNumber);
        }
        searchInputRef.current?.focus();
        return;
      }
      handleBrowserFind(direction === -1);
    },
    [activeSearchIndex, currentPage, handleBrowserFind, scrollToPage, searchMatches]
  );

  const handleRetry = React.useCallback(() => {
    setHasError(false);
    setImgLoading(true);
    setRetryKey((k) => k + 1);
  }, []);

  const handleClearSearch = React.useCallback(() => {
    setSearchText("");
    setDebouncedSearchText("");
    setSearchMatches([]);
    setSearchHighlightRects([]);
    setActiveSearchIndex(-1);
    setIsSearching(false);
    searchRunRef.current += 1;
    searchInputRef.current?.focus();
  }, []);

  // Debounce the query that actually drives the (expensive, per-page) PDF
  // text search — typing a multi-character query would otherwise queue one
  // full-document scan per keystroke. Clearing the field is applied
  // immediately so the result count doesn't lag behind an obviously-empty box.
  React.useEffect(() => {
    if (!searchText.trim()) {
      setDebouncedSearchText(searchText);
      return;
    }
    const timeoutId = window.setTimeout(() => setDebouncedSearchText(searchText), 250);
    return () => window.clearTimeout(timeoutId);
  }, [searchText]);

  // Track which page is at the top of the viewport as the user scrolls,
  // so the sidebar highlight follows the continuous scroll.
  const handleMainScroll = () => {
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const container = mainAreaRef.current;
      if (!container) return;
      const containerTop = container.getBoundingClientRect().top;
      const threshold = containerTop + container.clientHeight / 3;
      let visiblePage = 1;
      pageRefs.current.forEach((node, pageNumber) => {
        if (node.getBoundingClientRect().top <= threshold) {
          visiblePage = Math.max(visiblePage, pageNumber);
        }
      });
      setCurrentPage((current) => (current === visiblePage ? current : visiblePage));
    });
  };

  React.useEffect(
    () => () => {
      if (scrollRafRef.current !== null) window.cancelAnimationFrame(scrollRafRef.current);
    },
    []
  );

  const estimatedMainHeight = Math.round(MAIN_PAGE_WIDTH * ESTIMATED_PAGE_RATIO);
  const placeholderMainHeight = mainPageHeight ?? estimatedMainHeight;
  const thumbnailPlaceholderHeight = Math.round(
    THUMBNAIL_WIDTH * (placeholderMainHeight / MAIN_PAGE_WIDTH)
  );

  const fileUrl = openDoc?.entry.fileUrl || "";
  const fileName = openDoc?.entry.fileName || "Document";
  const uploadedLabel = formatUploadedAt(openDoc?.entry.uploadedAt);
  const showDuplicateLabel = hasDuplicateFlag(openDoc?.entry.isDuplicate);
  const titleText = [fileName, uploadedLabel, showDuplicateLabel ? "Duplicate" : null].filter(Boolean).join(" | ");
  const isMasked = Boolean(openDoc?.entry.isMasked);
  const isPdf =
    !isMasked &&
    (openDoc?.entry.mimeType?.toLowerCase() === "application/pdf" ||
      isPdfUrl(fileUrl));
  const isOpen = Boolean(openDoc);
  const trimmedSearchText = searchText.trim();
  const searchResultLabel = isSearching
    ? "Searching..."
    : trimmedSearchText
      ? `${activeSearchIndex >= 0 ? activeSearchIndex + 1 : 0}/${searchMatches.length}`
      : "0/0";

  // Reset view state when the displayed document changes.
  React.useEffect(() => {
    setNumPages(1);
    setCurrentPage(1);
    setMainPageHeight(null);
    setImgLoading(true);
    setHasError(false);
    setRetryKey(0);
    setZoom(1);
    setRotation(0);
    setSearchText("");
    setDebouncedSearchText("");
    setSearchMatches([]);
    setSearchHighlightRects([]);
    setActiveSearchIndex(-1);
    setIsSearching(false);
    setPdfLoadKey(0);
    pdfDocumentRef.current = null;
    searchRunRef.current += 1;
    pageRefs.current.clear();
    pageTextContentCache.current.clear();
    mainAreaRef.current?.scrollTo({ top: 0 });
  }, [fileUrl]);

  React.useEffect(() => {
    if (!isOpen) return;
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [isOpen, fileUrl]);

  React.useEffect(() => {
    const query = debouncedSearchText.trim();
    const pdfDocument = pdfDocumentRef.current;
    const runId = searchRunRef.current + 1;
    searchRunRef.current = runId;

    if (!isPdf || !query || !pdfDocument) {
      setSearchMatches([]);
      setSearchHighlightRects([]);
      setActiveSearchIndex(-1);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    const buildSearchIndex = async () => {
      const nextMatches: SearchMatch[] = [];
      const nextHighlightRects: SearchHighlightRect[] = [];
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        // A newer search (or zoom/rotation/document change) has superseded
        // this run — stop scanning further pages instead of finishing a scan
        // whose result will just be thrown away.
        if (searchRunRef.current !== runId) return;
        const page = await pdfDocument.getPage(pageNumber);
        // Text content is zoom/rotation-invariant, so it's cached per page —
        // a zoom or rotation change only needs to recompute highlight rects
        // below, not re-run the expensive per-page text extraction.
        let textContent = pageTextContentCache.current.get(pageNumber);
        if (!textContent) {
          textContent = await page.getTextContent();
          pageTextContentCache.current.set(pageNumber, textContent);
        }
        textContent.items.forEach((item, itemIndex) => {
          const text = item.str || "";
          const matchCount = countOccurrences(text, query);
          const lowerText = text.toLowerCase();
          const lowerQuery = query.toLowerCase();
          let charIndexInItem = lowerText.indexOf(lowerQuery);
          for (let matchIndexInItem = 0; matchIndexInItem < matchCount; matchIndexInItem += 1) {
            const matchIndex = nextMatches.length;
            nextMatches.push({ pageNumber, itemIndex, matchIndexInItem, charIndexInItem });
            const rect = buildHighlightRect(
              page,
              item,
              query,
              charIndexInItem,
              MAIN_PAGE_WIDTH * zoom,
              rotation
            );
            if (rect) {
              nextHighlightRects.push({ pageNumber, matchIndex, ...rect });
            }
            charIndexInItem = lowerText.indexOf(lowerQuery, charIndexInItem + lowerQuery.length);
          }
        });
      }

      if (searchRunRef.current !== runId) return;
      setSearchMatches(nextMatches);
      setSearchHighlightRects(nextHighlightRects);
      setActiveSearchIndex(-1);
      window.requestAnimationFrame(() => {
        if (searchRunRef.current === runId) {
          setActiveSearchIndex(nextMatches.length > 0 ? 0 : -1);
        }
      });
      setIsSearching(false);
      if (nextMatches.length > 0) {
        scrollToPage(nextMatches[0].pageNumber);
      }
    };

    buildSearchIndex().catch(() => {
      if (searchRunRef.current !== runId) return;
      setSearchMatches([]);
      setSearchHighlightRects([]);
      setActiveSearchIndex(-1);
      setIsSearching(false);
    });
  }, [debouncedSearchText, isPdf, pdfLoadKey, rotation, scrollToPage, zoom]);

  // Up/Down arrows scroll the preview area while the dialog is open.
  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      const target = event.target;
      // The search field is auto-focused while the dialog is open, so only
      // defer to it when it has text (up/down then jump the caret to the
      // start/end of the query); when it's empty, scroll the document.
      if (
        target instanceof HTMLElement &&
        (((target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
          target.value.length > 0) ||
          target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      mainAreaRef.current?.scrollBy({
        top: event.key === "ArrowDown" ? 120 : -120,
        behavior: "smooth",
      });
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!openDoc) return null;

  return (
    <Dialog open maxWidth="lg" fullWidth onClose={onClose}>
      <style>
        {`
          .quick-look-document .react-pdf__Page {
            position: relative;
          }
          .quick-look-search-overlay {
            position: absolute;
            inset: 0;
            pointer-events: none;
            z-index: 7;
          }
          .quick-look-search-overlay__hit {
            position: absolute;
            background: rgba(255, 242, 0, 0.72);
            border-radius: 1px;
            box-sizing: border-box;
            mix-blend-mode: multiply;
          }
          .quick-look-search-overlay__hit[data-quick-look-active-search="true"] {
            background: rgba(183, 214, 0, 0.78);
            box-shadow: none;
            mix-blend-mode: multiply;
          }
        `}
      </style>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid #e0e0e0",
          position: "relative",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 600,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
          title={titleText}
        >
          {showDuplicateLabel ? (
            <span
              style={{
                flexShrink: 0,
                fontWeight: 700,
                fontSize: 14,
                color: "#f39c12",
                whiteSpace: "nowrap",
              }}
            >
              Duplicate
            </span>
          ) : null}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{fileName}</span>
          {uploadedLabel ? (
            <span style={{ flexShrink: 0, fontWeight: 500, color: "#555" }}>{uploadedLabel}</span>
          ) : null}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: 12, whiteSpace: "nowrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "#ffffff",
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #dfe3e8",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
              height: 40,
            }}
          >
            <TextField
              inputRef={searchInputRef}
              size="small"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                handleNavigateSearch(event.shiftKey ? -1 : 1);
              }}
              placeholder="Search"
              inputProps={{ "aria-label": "Search Quick Look" }}
              sx={{
                width: "15ch",
                "& .MuiInputBase-root": {
                  height: 40,
                  backgroundColor: "#fff",
                  borderRadius: 0,
                },
                "& fieldset": {
                  border: 0,
                },
                "& input": {
                  fontSize: 13,
                  padding: "8px 12px",
                },
              }}
            />
            <span
              data-testid="quick-look-search-count"
              style={{
                minWidth: 56,
                padding: "0 12px",
                fontSize: 13,
                color: trimmedSearchText && searchMatches.length === 0 && !isSearching ? "#b00020" : "#222",
                textAlign: "center",
              }}
            >
              {searchResultLabel}
            </span>
            <span style={{ width: 1, alignSelf: "stretch", background: "#e7ebef" }} />
            <Tooltip title="Previous search result">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleNavigateSearch(-1)}
                  aria-label="Previous search result"
                  disabled={!trimmedSearchText || isSearching || searchMatches.length === 0}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next search result">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleNavigateSearch(1)}
                  aria-label="Next search result"
                  disabled={!trimmedSearchText || isSearching || searchMatches.length === 0}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <span style={{ width: 1, alignSelf: "stretch", background: "#e7ebef" }} />
            <Tooltip title="Clear search">
              <span>
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                  disabled={!searchText}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "#ffffff",
              borderRadius: 999,
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
              overflow: "hidden",
              border: "1px solid #eeeeee",
            }}
          >
            <Tooltip title="Zoom out">
              <span>
                <IconButton size="small" onClick={handleZoomOut} aria-label="Zoom out" disabled={zoom <= MIN_ZOOM}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <span style={{ width: 1, height: 24, background: "#eeeeee" }} />
            <Tooltip title="Actual size (100%)">
              <span>
                <IconButton size="small" onClick={handleActualSize} aria-label="Actual size (100%)" disabled={zoom === 1}>
                  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <circle cx="10" cy="10" r="6.2" fill="none" stroke="currentColor" strokeWidth="2.5" />
                    <path d="M14.7 14.7 21 21" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    <text x="10" y="13.2" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor">1</text>
                  </svg>
                </IconButton>
              </span>
            </Tooltip>
            <span style={{ width: 1, height: 24, background: "#eeeeee" }} />
            <Tooltip title="Zoom in">
              <span>
                <IconButton size="small" onClick={handleZoomIn} aria-label="Zoom in" disabled={zoom >= MAX_ZOOM}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </span>
          <Tooltip title="Rotate clockwise">
            <span>
              <IconButton size="small" onClick={handleRotateRight} aria-label="Rotate clockwise">
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </span>
        {sourceTotal > 1 && (
          <span
            style={{ fontSize: 13, color: "#666", whiteSpace: "nowrap", marginLeft: 12, marginRight: 4 }}
            data-testid="quick-look-position"
          >
            {sourcePosition} of {sourceTotal}
          </span>
        )}
        <Tooltip title="Close preview">
          <span style={{ marginLeft: 4 }}>
            <IconButton size="small" onClick={onClose} aria-label="Close preview">
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      <div style={{ display: "flex", height: "78vh", background: "#f5f5f5" }}>
        {isPdf ? (
          <Document
            key={retryKey}
            file={fileUrl}
            onLoadSuccess={(pdf) => {
              const loadedPdf = pdf as PdfDocument;
              setNumPages(loadedPdf.numPages);
              pdfDocumentRef.current = loadedPdf;
              setPdfLoadKey((key) => key + 1);
            }}
            onLoadError={() => setHasError(true)}
            className="quick-look-document"
            loading={
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
              </div>
            }
            error={
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: "#b00020" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <AiFillWarning style={{ marginRight: 6 }} /> Unable to load document
                </div>
                <Button size="small" variant="outlined" color="inherit" onClick={handleRetry} data-testid="quick-look-retry-btn">
                  Retry
                </Button>
              </div>
            }
          >
            {numPages > 1 ? (
              <div
                style={{
                  width: 140,
                  flexShrink: 0,
                  overflowY: "auto",
                  borderRight: "1px solid #e0e0e0",
                  background: "#fafafa",
                  padding: "8px 0",
                }}
              >
                {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
                  <div
                    key={pageNumber}
                    onClick={() => scrollToPage(pageNumber)}
                    style={{
                      cursor: "pointer",
                      margin: "0 auto 8px",
                      width: 114,
                      border:
                        pageNumber === currentPage
                          ? "2px solid #1976d2"
                          : "2px solid transparent",
                      borderRadius: 4,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <LazyThumbnail
                      pageNumber={pageNumber}
                      placeholderHeight={thumbnailPlaceholderHeight}
                    />
                    <div style={{ textAlign: "center", fontSize: 10, padding: "2px 0", color: "#555" }}>
                      {pageNumber}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <div
              ref={mainAreaRef}
              onScroll={handleMainScroll}
              style={{
                flex: 1,
                overflow: "auto",
                padding: 16,
              }}
            >
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
                <div
                  key={pageNumber}
                  ref={(node) => {
                    if (node) pageRefs.current.set(pageNumber, node);
                    else pageRefs.current.delete(pageNumber);
                  }}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  {Math.abs(pageNumber - currentPage) <= MAIN_RENDER_WINDOW ? (
                    <div
                      style={{
                        position: "relative",
                        width: MAIN_PAGE_WIDTH * zoom,
                        minHeight: placeholderMainHeight * zoom,
                      }}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={MAIN_PAGE_WIDTH * zoom}
                        rotate={rotation}
                        renderTextLayer={true}
                        renderAnnotationLayer={false}
                        onLoadSuccess={(page: { width?: number; height?: number }) => {
                          // Measure once so placeholders match the real page height.
                          if (page?.width && page?.height) {
                            setMainPageHeight(
                              (height) =>
                                height ??
                                Math.round((MAIN_PAGE_WIDTH * page.height!) / page.width!)
                            );
                          }
                        }}
                      />
                      <div className="quick-look-search-overlay" aria-hidden="true">
                        {searchHighlightRects
                          .filter((rect) => rect.pageNumber === pageNumber)
                          .map((rect) => {
                            const isActive = rect.matchIndex === activeSearchIndex;
                            return (
                              <span
                                key={`${rect.pageNumber}-${rect.matchIndex}`}
                                className="quick-look-search-overlay__hit"
                                data-quick-look-search-hit="true"
                                data-quick-look-active-search={isActive ? "true" : undefined}
                                style={{
                                  left: rect.left,
                                  top: rect.top,
                                  width: rect.width,
                                  height: rect.height,
                                }}
                              />
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <div
                      data-testid={`quick-look-page-placeholder-${pageNumber}`}
                      style={{
                        width: MAIN_PAGE_WIDTH * zoom,
                        height: placeholderMainHeight * zoom,
                        background: "#fff",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </Document>
        ) : (
          <div
            ref={mainAreaRef}
            style={{
              flex: 1,
              overflow: "auto",
              padding: 16,
            }}
          >
            {imgLoading && !hasError && (
              <Skeleton variant="rectangular" width="100%" height={600} animation="wave" />
            )}
            {hasError ? (
              <div style={{ color: "#b00020", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <AiFillWarning style={{ marginRight: 6 }} /> Unable to load document
                </div>
                <Button size="small" variant="outlined" color="inherit" onClick={handleRetry} data-testid="quick-look-retry-btn">
                  Retry
                </Button>
              </div>
            ) : (
              <img
                key={retryKey}
                src={fileUrl}
                alt={fileName}
                onLoad={() => setImgLoading(false)}
                onError={() => {
                  setImgLoading(false);
                  setHasError(true);
                }}
                style={{
                  width: `${zoom * 100}%`,
                  height: "auto",
                  maxWidth: "none",
                  transform: `rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                  display: imgLoading ? "none" : "block",
                }}
              />
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default QuickLookDialog;
